import { crypto as cv, type dto, type infra } from '@photo-vault/shared';
import { getDecryptPool } from '../viewer/decryptPool.js';
import { getCipher, putCipher } from '../viewer/mediaCache.js';
import { getCachedHash, putCachedHash } from './idbCache.js';

// Files at or above this size are treated as "large": we download them one
// at a time, never cache the cipher in IDB, and discard the plaintext as
// soon as the hash lands. Above HUGE_CLUSTER_THRESHOLD we don't even hash
// by default — the cluster is shown as "unverified" and the user has to
// opt in (since 5GB × N can mean GBs of egress charges from their bucket).
const LARGE_FILE_THRESHOLD = 100 * 1024 * 1024; // 100 MB
const HUGE_CLUSTER_THRESHOLD = 2 * 1024 * 1024 * 1024; // 2 GB total per cluster

// Cap on parallel HTTP+decrypt for "small" tier-2 work. Matches the existing
// download/decrypt pool sizing in upload/rotation flows.
const SMALL_PARALLELISM = 4;

export interface PhotoMetaInfo {
  photoId: string;
  albumId: string;
  rootAlbumId: string;
  size: number;
  filename: string;
  mimeType?: string;
  createdAt: string;
}

export interface DuplicateCluster {
  // sha256 of the plaintext (lowercase hex). For unverified large clusters
  // this is null — we have only the size match to go on.
  hash: string | null;
  size: number;
  // 'verified' = sha256 confirmed byte-identity. 'unverified' = same size,
  // contents not hashed (large-file opt-in pending).
  kind: 'verified' | 'unverified';
  photos: PhotoMetaInfo[];
}

export interface ScanProgress {
  // 'fetching' = pulling the photo-tree response.
  // 'tier1' = clustering by size + decrypting filenames for size-collision
  //           candidates.
  // 'tier2' = downloading + hashing the suspect set.
  // 'done' = finished.
  phase: 'fetching' | 'tier1' | 'tier2' | 'done';
  total: number;
  hashed: number;
  // Photos skipped for size policy (large/huge); kept in the result as
  // unverified clusters but not counted in `hashed`.
  unverified: number;
  current?: { photoId: string; size: number };
}

export interface ScanInput {
  rootId: string;
  albumKey: CryptoKey;
  token: string;
  metadata: infra.MetadataStore;
  onProgress?: (p: ScanProgress) => void;
  signal?: AbortSignal;
  // Override the "skip huge clusters" default. When true, even multi-GB
  // clusters get downloaded and hashed sequentially. UI exposes this as a
  // separate explicit action.
  verifyHugeClusters?: boolean;
}

export interface ScanResult {
  clusters: DuplicateCluster[];
  // Number of photos under the root, whether or not they were hashed.
  totalPhotos: number;
  // Photos eliminated by Tier-1 size uniqueness (no decrypt needed).
  uniqueBySize: number;
  // Photos hashed in Tier-2.
  hashed: number;
  // Bytes that would still need to be downloaded to upgrade unverified
  // clusters to verified. Zero when verifyHugeClusters was true.
  pendingVerificationBytes: number;
}

interface MetaResolved extends PhotoMetaInfo {
  encryptedMeta: string;
}

// Best-effort filename extraction. `encryptedMeta` is JSON {filename, mimeType, ...}
// once decrypted. On failure we fall back to the photo id.
async function decryptMeta(
  photo: dto.Photo,
  albumKey: CryptoKey,
): Promise<{ filename: string; mimeType?: string }> {
  try {
    const json = await cv.decryptText(photo.encryptedMeta, albumKey);
    const meta = JSON.parse(json) as { filename?: string; mimeType?: string };
    return {
      filename: meta.filename ?? photo.id,
      ...(meta.mimeType ? { mimeType: meta.mimeType } : {}),
    };
  } catch {
    return { filename: photo.id };
  }
}

// Drives the whole scan. Returns clusters of >=2 photos that share the same
// content (verified) or the same byte size with a content check pending
// (unverified). Caller is responsible for AbortSignal lifecycle.
export async function scanRootForDuplicates(input: ScanInput): Promise<ScanResult> {
  const {
    rootId,
    albumKey,
    token,
    metadata,
    onProgress,
    signal,
    verifyHugeClusters = false,
  } = input;

  signal?.throwIfAborted();
  onProgress?.({ phase: 'fetching', total: 0, hashed: 0, unverified: 0 });

  const { photos } = await metadata.fetchRootPhotosTree(rootId, token);
  signal?.throwIfAborted();

  // Tier 1 — size pre-clustering. Free: only `size` is in plaintext on the
  // Photo row. We keep a reference to the original Photo so we can decrypt
  // its meta later for the suspect set.
  const bySize = new Map<number, dto.Photo[]>();
  for (const p of photos) {
    const arr = bySize.get(p.size) ?? [];
    arr.push(p);
    bySize.set(p.size, arr);
  }

  const suspectSizes = [...bySize.entries()].filter(([, ps]) => ps.length > 1);
  const suspectPhotos = suspectSizes.flatMap(([, ps]) => ps);
  const uniqueBySize = photos.length - suspectPhotos.length;

  if (suspectPhotos.length === 0) {
    onProgress?.({ phase: 'done', total: 0, hashed: 0, unverified: 0 });
    return {
      clusters: [],
      totalPhotos: photos.length,
      uniqueBySize,
      hashed: 0,
      pendingVerificationBytes: 0,
    };
  }

  // Decrypt the filename for each suspect photo. encryptedMeta is small
  // (~150-300 cipher bytes) so doing this on the main thread is fine even
  // for thousands of suspects — the CPU cost is in the µs range per call.
  onProgress?.({ phase: 'tier1', total: suspectPhotos.length, hashed: 0, unverified: 0 });

  const resolved: MetaResolved[] = await Promise.all(
    suspectPhotos.map(async (p) => {
      const meta = await decryptMeta(p, albumKey);
      return {
        photoId: p.id,
        albumId: p.albumId,
        rootAlbumId: p.rootAlbumId,
        size: p.size,
        filename: meta.filename,
        ...(meta.mimeType ? { mimeType: meta.mimeType } : {}),
        createdAt: p.createdAt,
        encryptedMeta: p.encryptedMeta,
      };
    }),
  );
  signal?.throwIfAborted();

  // Re-bucket by size on the resolved set so each size group stays an
  // independent unit of Tier-2 work.
  const groupsBySize = new Map<number, MetaResolved[]>();
  for (const r of resolved) {
    const arr = groupsBySize.get(r.size) ?? [];
    arr.push(r);
    groupsBySize.set(r.size, arr);
  }

  const verifiedClusters: DuplicateCluster[] = [];
  const unverifiedClusters: DuplicateCluster[] = [];
  let pendingVerificationBytes = 0;
  let hashedCount = 0;
  let unverifiedCount = 0;

  // Total work for progress: sum of suspect counts that we'll actually hash.
  let plannedToHash = 0;
  for (const [size, group] of groupsBySize) {
    const clusterBytes = size * group.length;
    if (!verifyHugeClusters && clusterBytes > HUGE_CLUSTER_THRESHOLD) continue;
    plannedToHash += group.length;
  }

  onProgress?.({
    phase: 'tier2',
    total: plannedToHash,
    hashed: 0,
    unverified: 0,
  });

  for (const [size, group] of groupsBySize) {
    signal?.throwIfAborted();
    const clusterBytes = size * group.length;

    // Huge cluster policy: surface as unverified, don't download anything
    // unless the caller explicitly opted in.
    if (!verifyHugeClusters && clusterBytes > HUGE_CLUSTER_THRESHOLD) {
      unverifiedClusters.push({
        hash: null,
        size,
        kind: 'unverified',
        photos: group.map(stripMeta),
      });
      unverifiedCount += group.length;
      pendingVerificationBytes += clusterBytes;
      onProgress?.({
        phase: 'tier2',
        total: plannedToHash,
        hashed: hashedCount,
        unverified: unverifiedCount,
      });
      continue;
    }

    // Hash everyone in this size bucket and re-cluster by sha256. We hash
    // even singletons-after-tier1: they're the noise the user wants to see
    // disappear from the scan output.
    const hashes = new Map<string, MetaResolved[]>();

    if (size >= LARGE_FILE_THRESHOLD) {
      // Sequential, no IDB cipher cache. Each iteration drops its blob
      // ASAP via worker keepBytes:false.
      for (const photo of group) {
        signal?.throwIfAborted();
        const sha = await hashOne(metadata, photo, albumKey, token, /*useCipherCache*/ false);
        bumpHash(hashes, sha, photo);
        hashedCount += 1;
        onProgress?.({
          phase: 'tier2',
          total: plannedToHash,
          hashed: hashedCount,
          unverified: unverifiedCount,
          current: { photoId: photo.photoId, size: photo.size },
        });
      }
    } else {
      // Pool of SMALL_PARALLELISM concurrent hash-one calls. Standard cipher
      // cache stays enabled so subsequent grid views benefit.
      let cursor = 0;
      const workers: Promise<void>[] = [];
      const next = async () => {
        while (cursor < group.length) {
          const idx = cursor++;
          const photo = group[idx]!;
          signal?.throwIfAborted();
          const sha = await hashOne(metadata, photo, albumKey, token, /*useCipherCache*/ true);
          bumpHash(hashes, sha, photo);
          hashedCount += 1;
          onProgress?.({
            phase: 'tier2',
            total: plannedToHash,
            hashed: hashedCount,
            unverified: unverifiedCount,
            current: { photoId: photo.photoId, size: photo.size },
          });
        }
      };
      for (let i = 0; i < Math.min(SMALL_PARALLELISM, group.length); i++) workers.push(next());
      await Promise.all(workers);
    }

    // Promote sha-collisions of >=2 photos to verified clusters. Singletons
    // were noise (same size, different content) — we drop them.
    for (const [hash, members] of hashes) {
      if (members.length < 2) continue;
      verifiedClusters.push({
        hash,
        size,
        kind: 'verified',
        photos: members.map(stripMeta),
      });
    }
  }

  onProgress?.({
    phase: 'done',
    total: plannedToHash,
    hashed: hashedCount,
    unverified: unverifiedCount,
  });

  return {
    clusters: [...verifiedClusters, ...unverifiedClusters],
    totalPhotos: photos.length,
    uniqueBySize,
    hashed: hashedCount,
    pendingVerificationBytes,
  };
}

function bumpHash(hashes: Map<string, MetaResolved[]>, sha: string, photo: MetaResolved): void {
  const arr = hashes.get(sha) ?? [];
  arr.push(photo);
  hashes.set(sha, arr);
}

function stripMeta(m: MetaResolved): PhotoMetaInfo {
  const { encryptedMeta: _, ...rest } = m;
  return rest;
}

// Single-photo hash path. Hits the IDB hash cache first; on miss, presigns,
// downloads, decrypt+hashes inside the worker. `useCipherCache` controls
// whether the cipher buffer gets persisted in the shared media cache (off
// for large files so we don't blow the 500MB quota with one video).
async function hashOne(
  metadata: infra.MetadataStore,
  photo: MetaResolved,
  albumKey: CryptoKey,
  token: string,
  useCipherCache: boolean,
): Promise<string> {
  const cached = await getCachedHash(
    photo.rootAlbumId,
    photo.photoId,
    photo.encryptedMeta,
    photo.size,
  );
  if (cached) return cached.sha256;

  // Presign just this photo. The caller already chunks at the cluster
  // level, so paying for one batch call per file in this branch is fine —
  // it keeps each fetch independent and easy to abort.
  let url: string | null = null;
  await metadata.downloadsBatchAllStreaming(
    [{ albumId: photo.albumId, photoId: photo.photoId }],
    token,
    (chunk) => {
      const found = chunk.find((c) => c.photoId === photo.photoId);
      if (found) url = found.url;
    },
  );
  if (!url) throw new Error(`presigned URL not returned for ${photo.photoId}`);

  const cipher = await fetchCipher(photo.photoId, url, useCipherCache);
  const pool = getDecryptPool();
  // keepBytes:false for large files — the worker discards the plaintext
  // after digest, no ArrayBuffer transfer back to main thread.
  const { sha256 } = await pool.decryptAndHash(cipher, albumKey, { keepBytes: false });

  await putCachedHash(photo.rootAlbumId, photo.photoId, photo.encryptedMeta, photo.size, sha256);
  return sha256;
}

// Fetch the cipher bytes. For small photos we round-trip through the shared
// media cache (so a later grid view gets a free hit); for large ones we go
// direct to S3 to avoid cache thrash.
async function fetchCipher(
  photoId: string,
  url: string,
  useCipherCache: boolean,
): Promise<Uint8Array> {
  if (useCipherCache) {
    const hit = await getCipher(photoId);
    if (hit) return hit;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status} fetching cipher for ${photoId}`);
    const bytes = new Uint8Array(await res.arrayBuffer());
    void putCipher(photoId, bytes);
    return bytes;
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} fetching cipher for ${photoId}`);
  return new Uint8Array(await res.arrayBuffer());
}

export const __test = { LARGE_FILE_THRESHOLD, HUGE_CLUSTER_THRESHOLD };
